import React, { useCallback, useEffect, useState } from 'react';

import ChevronLeft from '@gravity-ui/icons/ChevronLeft';
import ChevronRight from '@gravity-ui/icons/ChevronRight';
import ChevronsLeft from '@gravity-ui/icons/ChevronsLeft';
import { Icon } from '@gravity-ui/uikit';
import { Button, Checkbox, useToaster } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { getAppsCards } from 'client/api';
import { AllAvailableAppsCard } from 'client/blocks/AllAvailableApps/AllAvailableAppsCard';
import { Logo } from 'client/blocks/icons/Logo';
import { IndexLayout } from 'client/blocks/Layout/Layout';
import { PageLoader } from 'client/blocks/Loader/PageLoader';
import { SearchAppInput } from 'client/blocks/SearchAppInput/SearchAppInput';
import { WithAppsMetaInfo, withAppsMetaInfo } from 'client/context/appsMetaInfoContext';
import { Channel, IAppCard } from 'client/model/skill';
import * as generalSettingsKeyset from 'i18n/GeneralSettings';
import * as keyset from 'i18n/SkillsIndex';
import debounce from 'lodash/debounce';

import styles from './AppsIndex.module.scss';
import { AppsIndexNavbar } from './AppsIndexNavbar';

interface InstallableAppsIndexProps {
    channel: Channel;
}

interface ICardsRequestOptions {
    channels: Channel[];
    page: number;
    listNotOwnedApps: true;
    pageSize: number;
    onlyPublished: boolean;
    query?: string;
}

const t = i18n(keyset);
const tGeneral = i18n(generalSettingsKeyset);
const PAGE_SIZE = 50;

const AllAvailableAppsByChannel = ({ channel }: InstallableAppsIndexProps & WithAppsMetaInfo) => {
    const [cards, setCards] = useState<IAppCard[]>();
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState<string | undefined>();
    const [onlyPublishedFilter, setOnlyPublishedFilter] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [hasPrevPage, setHasPrevPage] = useState(false);

    const { add: addToast } = useToaster();

    const loadPage = useCallback(
        async (requestOptions: ICardsRequestOptions) => {
            setIsLoading(true);
            try {
                const result = await getAppsCards(requestOptions);

                if (result && result.cards) {
                    setCards(result.cards);
                    setHasNextPage(!!result.paging_info?.next);
                    setHasPrevPage(!!result.paging_info?.previous);
                }
            } catch (error) {
                let errorMessage;
                if (error && typeof error === 'object' && 'message' in error) {
                    errorMessage = error.message;
                }
                addToast({
                    title: tGeneral('error'),
                    content: errorMessage as React.ReactNode,
                    theme: 'danger',
                    name: 'CARDS_FETCH_ERROR',
                    autoHiding: false,
                    isClosable: true,
                });
            } finally {
                setIsLoading(false);
            }
        },
        [addToast],
    );

    useEffect(() => {
        loadPage({
            channels: [channel],
            page: currentPage,
            listNotOwnedApps: true,
            pageSize: PAGE_SIZE,
            onlyPublished: onlyPublishedFilter,
            query: searchQuery,
        });
    }, [channel, currentPage, onlyPublishedFilter, loadPage, searchQuery]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleSearchDebounced = useCallback(
        debounce((updatedSearchQuery?: string) => {
            setCurrentPage(1);
            setSearchQuery(updatedSearchQuery);
        }, 400),
        [channel],
    );

    const handlePrevPage = () => {
        if (hasPrevPage && currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (hasNextPage) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handleChangeOnlyPublished = (checked: boolean) => {
        setOnlyPublishedFilter(checked);
        setCurrentPage(1);
    };

    return (
        <IndexLayout>
            <article className={styles.appsIndex__container}>
                <AppsIndexNavbar channel={channel} />
                <div className={styles.searchContainer}>
                    <Logo size="l" />
                    <SearchAppInput channel={channel} onChange={handleSearchDebounced} />
                    <Checkbox checked={onlyPublishedFilter} onUpdate={handleChangeOnlyPublished} size="l">
                        {t('show-only-published')}
                    </Checkbox>
                </div>
                <div className={styles.cadsList}>
                    {cards?.map((card) => (
                        <AllAvailableAppsCard key={card.id} card={card} />
                    ))}
                </div>
                {(hasPrevPage || hasNextPage) && (
                    <div className={styles.pagination}>
                        <Button onClick={() => setCurrentPage(1)} disabled={!hasPrevPage} size="m" view="outlined">
                            <Icon data={ChevronsLeft} />
                        </Button>
                        <Button onClick={handlePrevPage} disabled={!hasPrevPage} size="m" view="outlined">
                            <Icon data={ChevronLeft} />
                        </Button>
                        <span className={styles.pageInfo}>{currentPage}</span>
                        <Button onClick={handleNextPage} disabled={!hasNextPage} size="m" view="outlined">
                            <Icon data={ChevronRight} />
                        </Button>
                    </div>
                )}
                {(!cards || isLoading) && (
                    <div className={styles.veilContainer}>
                        <PageLoader sticky />
                    </div>
                )}
            </article>
        </IndexLayout>
    );
};

export default withAppsMetaInfo(AllAvailableAppsByChannel);
